import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Typography,
    TextField,
    Button
} from '@mui/material';
import {
    EditOutlinedIcon
} from 'icons/materialIcons';

const TeamV2MemberAboutContainer = ({ memberData, setMemberData, setSelectedItem, isNewMember = false }) => {
    const [formData, setFormData] = useState(memberData);

    // Update form data when memberData changes
    useEffect(() => {
        setFormData(memberData);
    }, [memberData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = () => {
        setMemberData(formData);
        if (isNewMember) {
            // Navigate to contact links section for new members
            setSelectedItem('contact-links');
        }
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: 2,
                    backgroundColor: '#ffffff'
                }}
            >
                {/* Header Section */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        marginBottom: 2,
                    }}
                >
                    <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px' }} gutterBottom align="left">About</Typography>
                </Box>

                {/* Content Section */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: 2,
                        borderRadius: 1,
                    }}
                >
                    {/* Form Fields Container */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 2,
                        }}
                    >
                        {/* Left-aligned Member Name */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#4f4f4f" }}>
                                Member Name:
                            </Typography>
                            <Button
                                variant="text"
                                sx={{
                                    textTransform: 'none',
                                    padding: '4px 8px',
                                    width: "150px",
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    backgroundColor: "#f7f7f7",
                                    color: "#212121",
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    textAlign: 'left',
                                }}
                                endIcon={<EditOutlinedIcon sx={{ color: '#828282', fontSize: "14px" }} />}
                            >
                                {formData.firstName && formData.lastName ? `${formData.firstName} ${formData.lastName}` : 'Member Name'}
                            </Button>
                        </Box>

                        {/* Right-aligned Member Status */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#4f4f4f", display: 'flex', alignItems: 'center' }}>
                                Status:
                            </Typography>
                            <TextField
                                select
                                SelectProps={{
                                    native: true
                                }}
                                value={formData.status || 'invited'}
                                onChange={(e) => handleInputChange('status', e.target.value)}
                                sx={{
                                    fontSize: '0.7rem',
                                    minWidth: 120,
                                    backgroundColor: "#f7f7f7",
                                    color: "#212121",
                                    fontWeight: 600,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: "none",
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        border: "none",
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        border: "none",
                                    },
                                }}
                            >
                                <option value="invited">Invited</option>
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                            </TextField>
                        </Box>
                    </Box>

                    {/* Profile Photo Placeholder */}
                    <Box
                        sx={{
                            width: '100%',
                            height: '200px',
                            backgroundColor: '#f7f7f7',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 4,
                            border: '2px dashed #e0e0e0'
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                            Profile Photo Upload Area
                        </Typography>
                    </Box>

                    {/* Name, Location, Job Title, Company, and Bio Inputs */}
                    <Grid container spacing={2} sx={{ marginTop: 4 }}>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                First Name
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="First Name"
                                name="firstName"
                                value={formData.firstName || ''}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                        '& input::placeholder': {
                                            fontSize: '0.8rem',
                                            fontWeight: '300',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                Last Name
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Last Name"
                                name="lastName"
                                value={formData.lastName || ''}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                        '& input::placeholder': {
                                            fontSize: '0.8rem',
                                            fontWeight: '300',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                Email Address
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Email Address"
                                name="email"
                                type="email"
                                value={formData.email || ''}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                        '& input::placeholder': {
                                            fontSize: '0.8rem',
                                            fontWeight: '300',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                Job Title
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Job Title"
                                name="jobTitle"
                                value={formData.jobTitle || ''}
                                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                        '& input::placeholder': {
                                            fontSize: '0.8rem',
                                            fontWeight: '300',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                Company
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Company"
                                name="company"
                                value={formData.company || ''}
                                onChange={(e) => handleInputChange('company', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                        '& input::placeholder': {
                                            fontSize: '0.8rem',
                                            fontWeight: '300',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                Location
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Location"
                                name="location"
                                value={formData.location || ''}
                                onChange={(e) => handleInputChange('location', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                        '& input::placeholder': {
                                            fontSize: '0.8rem',
                                            fontWeight: '300',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                Bio
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Bio"
                                name="bio"
                                multiline
                                rows={3}
                                value={formData.bio || ''}
                                onChange={(e) => handleInputChange('bio', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                        '& textarea::placeholder': {
                                            fontSize: '0.8rem',
                                            fontWeight: '300',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Action Buttons for New Members */}
                    {isNewMember && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            marginTop: '2rem',
                            paddingTop: '2rem',
                            borderTop: '1px solid #e0e0e0'
                        }}>
                            <Button
                                variant="outlined"
                                onClick={() => setSelectedItem('contact-links')}
                                sx={{ textTransform: 'none' }}
                            >
                                Skip for Now
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    backgroundColor: '#000',
                                    textTransform: 'none',
                                    '&:hover': { backgroundColor: '#333' }
                                }}
                            >
                                Continue to Links
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </form>
    );
};

export default TeamV2MemberAboutContainer;
